import { BLOCK_CATALOG_V1, WORLD_FORMAT_V1, } from "./contracts.js";
import { copyUint8ArrayOfExactLength, copyUint8ArrayWithinLimit, isUint8Array, readUint8ArrayInternalByteLength, sha256DigestAfterLengthGuard, } from "./internal/validated-brand.js";
const MODIFIED_RECORD_PREFIX_BYTES = 528;
const MODIFIED_RECORD_TRAILER_BYTES = 4;
const MODIFIED_RECORD_MIN_BYTES = 537;
const MODIFIED_RECORD_MAX_BYTES = 25_108;
const VOXELS_PER_SUBCHUNK = 4_096;
const MODIFICATION_MASK_BYTES = 512;
const SNAPSHOT_HEADER_BYTES = 64;
const DIRECTORY_ENTRY_BYTES = 16;
const SNAPSHOT_TRAILER_BYTES = 36;
const SNAPSHOT_MAX_BYTES = 16 * 1_024 * 1_024;
const DELTA_SHARED_HEADER_BYTES = 40;
const DELTA_LOCAL_HEADER_BYTES = 56;
const DELTA_TRAILER_BYTES = 4;
const DELTA_MAX_BYTES = 1_024 * 1_024;
const SHARED_MANIFEST_HEADER_BYTES = 166;
const SHARED_MANIFEST_ENTRY_BYTES = 80;
const LOCAL_MANIFEST_HEADER_BYTES = 174;
const LOCAL_MANIFEST_ENTRY_BYTES = 56;
const MANIFEST_TRAILER_BYTES = 36;
const MANIFEST_MAX_BYTES = 16 * 1_024 * 1_024;
const MAX_SUBCHUNK_RECORDS = 8;
const MAX_MANIFEST_ENTRIES = 256;
const UINT32_MAX = 0xffff_ffff;
const numberIsInteger = Number.isInteger;
const MAGIC_HWCS = Object.freeze([0x48, 0x57, 0x43, 0x53]);
const MAGIC_HWLS = Object.freeze([0x48, 0x57, 0x4c, 0x53]);
const MAGIC_HWCD = Object.freeze([0x48, 0x57, 0x43, 0x44]);
const MAGIC_HWLD = Object.freeze([0x48, 0x57, 0x4c, 0x44]);
const MAGIC_HWMS = Object.freeze([0x48, 0x57, 0x4d, 0x53]);
const MAGIC_HWLM = Object.freeze([0x48, 0x57, 0x4c, 0x4d]);
const OBJECT_KEY_DOMAIN = Uint8Array.from("hearthwild-r2-chunk-v1", (character) => character.charCodeAt(0));
function decodeFailure(code, byteOffset) {
    return Object.freeze({
        ok: false,
        error: Object.freeze({ code, byteOffset }),
    });
}
function decodeSuccess(value) {
    return Object.freeze({ ok: true, value });
}
function requireChecked(result, label) {
    if (!result.ok) {
        throw new TypeError(`${label} failed checked-value validation: ${result.error.code}.`);
    }
    return result.value;
}
function invalidEncode(label) {
    throw new TypeError(`Cannot encode a non-canonical ${label}.`);
}
function copyDecodeInput(bytes, maximumBytes) {
    return copyUint8ArrayWithinLimit(bytes, maximumBytes);
}
function writeMagic(bytes, magic) {
    for (let index = 0; index < magic.length; index += 1) {
        const byte = magic[index];
        if (byte === undefined)
            invalidEncode("container magic");
        bytes[index] = byte;
    }
}
function hasMagic(bytes, magic) {
    for (let index = 0; index < magic.length; index += 1) {
        if (bytes[index] !== magic[index])
            return false;
    }
    return true;
}
function equalBytes(left, right) {
    const leftByteLength = readUint8ArrayInternalByteLength(left);
    const rightByteLength = readUint8ArrayInternalByteLength(right);
    if (leftByteLength === null || rightByteLength === null
        || leftByteLength !== rightByteLength)
        return false;
    let difference = 0;
    for (let index = 0; index < leftByteLength; index += 1) {
        difference |= (left[index] ?? 0) ^ (right[index] ?? 0);
    }
    return difference === 0;
}
function popcountMask(mask) {
    let count = 0;
    for (const byte of mask) {
        let value = byte;
        while (value !== 0) {
            value &= value - 1;
            count += 1;
        }
    }
    return count;
}
function compareChunkOrder(left, right) {
    if (left.chunkZ !== right.chunkZ)
        return left.chunkZ - right.chunkZ;
    return left.chunkX - right.chunkX;
}
function checkedCrc32c(checksumProvider, bytes) {
    try {
        const providerInput = copyUint8ArrayWithinLimit(bytes, MANIFEST_MAX_BYTES);
        if (providerInput === null)
            return null;
        const value = checksumProvider.crc32c(providerInput);
        return numberIsInteger(value) && value >= 0 && value <= UINT32_MAX ? value : null;
    }
    catch {
        return null;
    }
}
async function checkedSha256(checksumProvider, bytes) {
    try {
        const providerInput = copyUint8ArrayWithinLimit(bytes, MANIFEST_MAX_BYTES);
        if (providerInput === null)
            return null;
        const digest = await checksumProvider.sha256(providerInput);
        const copy = copyUint8ArrayOfExactLength(digest, 32);
        return copy === null ? null : sha256DigestAfterLengthGuard(copy);
    }
    catch {
        return null;
    }
}
function createCodec(dependencies) {
    if (typeof dependencies !== "object" || dependencies === null) {
        throw new TypeError("World-format codec dependencies are required.");
    }
    const providedCheckedValues = dependencies.checkedValues;
    const providedChecksumProvider = dependencies.checksumProvider;
    if (typeof providedCheckedValues !== "object" || providedCheckedValues === null
        || typeof providedChecksumProvider !== "object" || providedChecksumProvider === null
        || typeof providedCheckedValues.int32 !== "function"
        || typeof providedCheckedValues.uint32 !== "function"
        || typeof providedCheckedValues.uint64 !== "function"
        || typeof providedCheckedValues.worldSeed !== "function"
        || typeof providedCheckedValues.worldRevision !== "function"
        || typeof providedCheckedValues.localSandboxRevision !== "function"
        || typeof providedCheckedValues.subchunkY !== "function"
        || typeof providedCheckedValues.blockId !== "function"
        || typeof providedCheckedValues.sharedWorldUuid !== "function"
        || typeof providedCheckedValues.localSandboxUuid !== "function"
        || typeof providedCheckedValues.sha256Digest !== "function"
        || typeof providedCheckedValues.sha256Prefix24 !== "function"
        || typeof providedCheckedValues.modificationMask !== "function"
        || typeof providedCheckedValues.createSharedMetadata !== "function"
        || typeof providedCheckedValues.createLocalSandboxMetadata !== "function"
        || typeof providedChecksumProvider.crc32c !== "function"
        || typeof providedChecksumProvider.sha256 !== "function") {
        throw new TypeError("Checked values and checksum providers are required.");
    }
    const checkedValues = Object.freeze({
        int16: providedCheckedValues.int16.bind(providedCheckedValues),
        int32: providedCheckedValues.int32.bind(providedCheckedValues),
        uint32: providedCheckedValues.uint32.bind(providedCheckedValues),
        int64: providedCheckedValues.int64.bind(providedCheckedValues),
        uint64: providedCheckedValues.uint64.bind(providedCheckedValues),
        worldSeed: providedCheckedValues.worldSeed.bind(providedCheckedValues),
        worldRevision: providedCheckedValues.worldRevision.bind(providedCheckedValues),
        localSandboxRevision: providedCheckedValues.localSandboxRevision.bind(providedCheckedValues),
        subchunkY: providedCheckedValues.subchunkY.bind(providedCheckedValues),
        localVoxelAxis: providedCheckedValues.localVoxelAxis.bind(providedCheckedValues),
        voxelIndex: providedCheckedValues.voxelIndex.bind(providedCheckedValues),
        blockId: providedCheckedValues.blockId.bind(providedCheckedValues),
        placeableBlockId: providedCheckedValues.placeableBlockId.bind(providedCheckedValues),
        uuidBytes: providedCheckedValues.uuidBytes.bind(providedCheckedValues),
        sharedWorldUuid: providedCheckedValues.sharedWorldUuid.bind(providedCheckedValues),
        localSandboxUuid: providedCheckedValues.localSandboxUuid.bind(providedCheckedValues),
        sha256Digest: providedCheckedValues.sha256Digest.bind(providedCheckedValues),
        sha256Prefix24: providedCheckedValues.sha256Prefix24.bind(providedCheckedValues),
        modificationMask: providedCheckedValues.modificationMask.bind(providedCheckedValues),
        biomeConfiguration: providedCheckedValues.biomeConfiguration.bind(providedCheckedValues),
        createSharedMetadata: providedCheckedValues.createSharedMetadata.bind(providedCheckedValues),
        createLocalSandboxMetadata: providedCheckedValues.createLocalSandboxMetadata.bind(providedCheckedValues),
    });
    const checksumProvider = Object.freeze({
        crc32c: providedChecksumProvider.crc32c.bind(providedChecksumProvider),
        sha256: providedChecksumProvider.sha256.bind(providedChecksumProvider),
    });
    function validateRecordForEncoding(value) {
        if (typeof value !== "object" || value === null
            || !Array.isArray(value.palette) || !Array.isArray(value.modifiedValues)) {
            return invalidEncode("modified-subchunk record");
        }
        const subchunkY = requireChecked(checkedValues.subchunkY(value.subchunkY), "subchunkY");
        const revision = requireChecked(checkedValues.uint64(value.revision), "revision");
        const modificationMask = requireChecked(checkedValues.modificationMask(value.modificationMask), "modificationMask");
        const modificationCount = popcountMask(modificationMask);
        if (modificationCount === 0 || modificationCount > VOXELS_PER_SUBCHUNK
            || value.modifiedValues.length !== modificationCount) {
            return invalidEncode("modified-subchunk modification sequence");
        }
        const modifiedValues = [];
        const present = new Uint8Array(BLOCK_CATALOG_V1.length);
        for (const candidate of value.modifiedValues) {
            const blockId = requireChecked(checkedValues.blockId(candidate), "modified block ID");
            modifiedValues.push(blockId);
            present[blockId] = 1;
        }
        const palette = [];
        for (let blockId = 0; blockId < present.byteLength; blockId += 1) {
            if (present[blockId] === 1) {
                const checked = checkedValues.blockId(blockId);
                if (!checked.ok)
                    return invalidEncode("block palette");
                palette.push(checked.value);
            }
        }
        if (value.palette.length !== palette.length) {
            return invalidEncode("block palette");
        }
        for (let index = 0; index < palette.length; index += 1) {
            if (value.palette[index] !== palette[index])
                return invalidEncode("block palette");
        }
        return Object.freeze({
            subchunkY,
            revision,
            modificationMask,
            palette: Object.freeze(palette),
            modifiedValues: Object.freeze(modifiedValues),
        });
    }
    function encodeRecord(value) {
        const record = validateRecordForEncoding(value);
        const paletteIndices = new Int16Array(BLOCK_CATALOG_V1.length);
        paletteIndices.fill(-1);
        for (let index = 0; index < record.palette.length; index += 1) {
            const blockId = record.palette[index];
            if (blockId === undefined)
                return invalidEncode("block palette");
            paletteIndices[blockId] = index;
        }
        const runLengths = [];
        const runPaletteIndices = [];
        for (const blockId of record.modifiedValues) {
            const paletteIndex = paletteIndices[blockId];
            if (paletteIndex === undefined || paletteIndex < 0) {
                return invalidEncode("modified-subchunk palette index");
            }
            const lastRun = runPaletteIndices.length - 1;
            if (lastRun >= 0 && runPaletteIndices[lastRun] === paletteIndex) {
                const previousLength = runLengths[lastRun];
                if (previousLength === undefined || previousLength >= VOXELS_PER_SUBCHUNK) {
                    return invalidEncode("modified-subchunk run");
                }
                runLengths[lastRun] = previousLength + 1;
            }
            else {
                runPaletteIndices.push(paletteIndex);
                runLengths.push(1);
            }
        }
        const paletteIndexWidth = record.palette.length <= 256 ? 1 : 2;
        const recordLength = MODIFIED_RECORD_PREFIX_BYTES
            + record.palette.length * 2
            + runLengths.length * (2 + paletteIndexWidth)
            + MODIFIED_RECORD_TRAILER_BYTES;
        if (recordLength < MODIFIED_RECORD_MIN_BYTES || recordLength > MODIFIED_RECORD_MAX_BYTES) {
            return invalidEncode("modified-subchunk length");
        }
        const bytes = new Uint8Array(recordLength);
        const view = new DataView(bytes.buffer);
        view.setUint8(0, record.subchunkY);
        view.setUint8(1, paletteIndexWidth);
        view.setUint16(2, record.palette.length, true);
        view.setUint16(4, record.modifiedValues.length, true);
        view.setUint16(6, runLengths.length, true);
        view.setBigUint64(8, record.revision, true);
        bytes.set(record.modificationMask, 16);
        let offset = MODIFIED_RECORD_PREFIX_BYTES;
        for (const blockId of record.palette) {
            view.setUint16(offset, blockId, true);
            offset += 2;
        }
        for (let index = 0; index < runLengths.length; index += 1) {
            const runLength = runLengths[index];
            const paletteIndex = runPaletteIndices[index];
            if (runLength === undefined || paletteIndex === undefined) {
                return invalidEncode("modified-subchunk run");
            }
            view.setUint16(offset, runLength, true);
            if (paletteIndexWidth === 1)
                view.setUint8(offset + 2, paletteIndex);
            else
                view.setUint16(offset + 2, paletteIndex, true);
            offset += 2 + paletteIndexWidth;
        }
        if (offset !== recordLength - MODIFIED_RECORD_TRAILER_BYTES) {
            return invalidEncode("modified-subchunk packing");
        }
        const crc32c = checkedCrc32c(checksumProvider, bytes.subarray(0, offset));
        if (crc32c === null)
            return invalidEncode("CRC32C provider result");
        view.setUint32(offset, crc32c, true);
        return Object.freeze({
            subchunkY: record.subchunkY,
            revision: record.revision,
            bytes,
            crc32c,
        });
    }
    function parseRecord(bytes, revisionChecker) {
        const input = copyDecodeInput(bytes, MODIFIED_RECORD_MAX_BYTES);
        if (input === null || input.byteLength < MODIFIED_RECORD_MIN_BYTES) {
            return decodeFailure("INVALID_LENGTH", null);
        }
        const view = new DataView(input.buffer);
        const subchunkYRaw = view.getUint8(0);
        const paletteIndexWidth = view.getUint8(1);
        const paletteCount = view.getUint16(2, true);
        const modificationCount = view.getUint16(4, true);
        const runCount = view.getUint16(6, true);
        if (subchunkYRaw > 7)
            return decodeFailure("INVALID_COUNT", 0);
        if (paletteCount < 1 || paletteCount > VOXELS_PER_SUBCHUNK) {
            return decodeFailure("INVALID_COUNT", 2);
        }
        const expectedWidth = paletteCount <= 256 ? 1 : 2;
        if (paletteIndexWidth !== expectedWidth)
            return decodeFailure("INVALID_COUNT", 1);
        if (modificationCount < 1 || modificationCount > VOXELS_PER_SUBCHUNK) {
            return decodeFailure("INVALID_COUNT", 4);
        }
        if (runCount < 1 || runCount > modificationCount) {
            return decodeFailure("INVALID_COUNT", 6);
        }
        const expectedLength = 532 + paletteCount * 2 + runCount * (2 + paletteIndexWidth);
        if (expectedLength !== input.byteLength) {
            return decodeFailure(expectedLength < input.byteLength ? "TRAILING_BYTES" : "INVALID_LENGTH", Math.min(expectedLength, input.byteLength));
        }
        const storedCrc = view.getUint32(input.byteLength - 4, true);
        const actualCrc = checkedCrc32c(checksumProvider, input.subarray(0, input.byteLength - 4));
        if (actualCrc === null || storedCrc !== actualCrc) {
            return decodeFailure("CHECKSUM_MISMATCH", input.byteLength - 4);
        }
        const maskBytes = input.slice(16, 16 + MODIFICATION_MASK_BYTES);
        if (popcountMask(maskBytes) !== modificationCount) {
            return decodeFailure("INVALID_COUNT", 16);
        }
        const checkedMask = checkedValues.modificationMask(maskBytes);
        if (!checkedMask.ok)
            return decodeFailure("INVALID_LENGTH", 16);
        const checkedSubchunkY = checkedValues.subchunkY(subchunkYRaw);
        if (!checkedSubchunkY.ok)
            return decodeFailure("INVALID_COUNT", 0);
        const checkedRevision = revisionChecker(view.getBigUint64(8, true));
        if (!checkedRevision.ok)
            return decodeFailure("INVALID_REVISION", 8);
        const palette = [];
        let offset = MODIFIED_RECORD_PREFIX_BYTES;
        let previousBlockId = -1;
        for (let index = 0; index < paletteCount; index += 1) {
            const blockIdRaw = view.getUint16(offset, true);
            const checkedBlockId = checkedValues.blockId(blockIdRaw);
            if (!checkedBlockId.ok)
                return decodeFailure("INVALID_BLOCK_ID", offset);
            if (blockIdRaw <= previousBlockId)
                return decodeFailure("INVALID_ORDER", offset);
            palette.push(checkedBlockId.value);
            previousBlockId = blockIdRaw;
            offset += 2;
        }
        const modifiedValues = [];
        const usedPaletteEntries = new Uint8Array(paletteCount);
        let previousPaletteIndex = -1;
        let totalRunLength = 0;
        for (let index = 0; index < runCount; index += 1) {
            const runOffset = offset;
            const runLength = view.getUint16(offset, true);
            const paletteIndex = paletteIndexWidth === 1
                ? view.getUint8(offset + 2)
                : view.getUint16(offset + 2, true);
            if (runLength < 1 || totalRunLength + runLength > modificationCount) {
                return decodeFailure("INVALID_COUNT", runOffset);
            }
            if (paletteIndex >= paletteCount) {
                return decodeFailure("INVALID_BLOCK_ID", offset + 2);
            }
            if (paletteIndex === previousPaletteIndex) {
                return decodeFailure("INVALID_ORDER", offset + 2);
            }
            const blockId = palette[paletteIndex];
            if (blockId === undefined)
                return decodeFailure("INVALID_BLOCK_ID", offset + 2);
            usedPaletteEntries[paletteIndex] = 1;
            for (let repeated = 0; repeated < runLength; repeated += 1) {
                modifiedValues.push(blockId);
            }
            totalRunLength += runLength;
            previousPaletteIndex = paletteIndex;
            offset += 2 + paletteIndexWidth;
        }
        if (totalRunLength !== modificationCount)
            return decodeFailure("INVALID_COUNT", 6);
        if (offset !== input.byteLength - 4)
            return decodeFailure("TRAILING_BYTES", offset);
        for (let index = 0; index < paletteCount; index += 1) {
            if (usedPaletteEntries[index] !== 1) {
                return decodeFailure("INVALID_ORDER", MODIFIED_RECORD_PREFIX_BYTES + index * 2);
            }
        }
        const value = Object.freeze({
            subchunkY: checkedSubchunkY.value,
            revision: checkedRevision.value,
            modificationMask: checkedMask.value,
            palette: Object.freeze(palette),
            modifiedValues: Object.freeze(modifiedValues),
        });
        return decodeSuccess(value);
    }
    const scopeDecoders = Object.freeze({
        shared_world: Object.freeze({
            decode: (bytes) => parseRecord(bytes, (value) => checkedValues.worldRevision(value)),
        }),
        local_sandbox: Object.freeze({
            decode: (bytes) => parseRecord(bytes, (value) => checkedValues.localSandboxRevision(value)),
        }),
    });
    function encodeModifiedSubchunk(value) {
        return encodeRecord(value).bytes;
    }
    function decodeModifiedSubchunk(bytes, expectedScope) {
        if (expectedScope !== "shared_world" && expectedScope !== "local_sandbox") {
            return decodeFailure("INVALID_HEADER", null);
        }
        return scopeDecoders[expectedScope].decode(bytes);
    }
    function validateRecordListForEncoding(records, minimumRevision, maximumRevision) {
        if (!Array.isArray(records) || records.length > MAX_SUBCHUNK_RECORDS) {
            return invalidEncode("subchunk record list");
        }
        const encodedRecords = [];
        let previousSubchunkY = -1;
        for (const record of records) {
            const encoded = encodeRecord(record);
            if (encoded.subchunkY <= previousSubchunkY
                || encoded.revision < minimumRevision
                || encoded.revision > maximumRevision) {
                return invalidEncode("ordered subchunk record list");
            }
            encodedRecords.push(encoded);
            previousSubchunkY = encoded.subchunkY;
        }
        return Object.freeze(encodedRecords);
    }
    async function encodeSnapshotCommon(magic, identity, chunkXValue, chunkZValue, throughRevisionValue, records) {
        const chunkX = requireChecked(checkedValues.int32(chunkXValue), "snapshot chunkX");
        const chunkZ = requireChecked(checkedValues.int32(chunkZValue), "snapshot chunkZ");
        if (chunkX < WORLD_FORMAT_V1.worldMinChunkX
            || chunkX > WORLD_FORMAT_V1.worldMaxChunkX
            || chunkZ < WORLD_FORMAT_V1.worldMinChunkZ
            || chunkZ > WORLD_FORMAT_V1.worldMaxChunkZ) {
            return invalidEncode("snapshot chunk bounds");
        }
        const throughRevision = requireChecked(checkedValues.uint64(throughRevisionValue), "snapshot through revision");
        const identityCopy = copyUint8ArrayOfExactLength(identity, 16);
        if (identityCopy === null)
            return invalidEncode("snapshot identity");
        const zeroRevision = requireChecked(checkedValues.uint64(0n), "zero revision");
        const encodedRecords = validateRecordListForEncoding(records, zeroRevision, throughRevision);
        let recordsByteLength = 0;
        for (const record of encodedRecords)
            recordsByteLength += record.bytes.byteLength;
        const recordsOffset = SNAPSHOT_HEADER_BYTES
            + encodedRecords.length * DIRECTORY_ENTRY_BYTES;
        const bodyEndOffset = recordsOffset + recordsByteLength;
        const totalLength = bodyEndOffset + SNAPSHOT_TRAILER_BYTES;
        if (totalLength > SNAPSHOT_MAX_BYTES)
            return invalidEncode("chunk snapshot length");
        const bytes = new Uint8Array(totalLength);
        const view = new DataView(bytes.buffer);
        writeMagic(bytes, magic);
        view.setUint16(4, WORLD_FORMAT_V1.worldFormatVersion, true);
        view.setUint16(6, WORLD_FORMAT_V1.generatorVersion, true);
        view.setUint16(8, WORLD_FORMAT_V1.blockCatalogVersion, true);
        view.setUint16(10, SNAPSHOT_HEADER_BYTES, true);
        view.setUint16(12, 0, true);
        view.setUint8(14, encodedRecords.length);
        view.setUint8(15, DIRECTORY_ENTRY_BYTES);
        bytes.set(identityCopy, 16);
        view.setInt32(32, chunkX, true);
        view.setInt32(36, chunkZ, true);
        view.setBigUint64(40, throughRevision, true);
        view.setUint32(48, SNAPSHOT_HEADER_BYTES, true);
        view.setUint32(52, recordsOffset, true);
        view.setUint32(56, bodyEndOffset, true);
        view.setUint32(60, 0, true);
        let recordOffset = recordsOffset;
        for (let index = 0; index < encodedRecords.length; index += 1) {
            const record = encodedRecords[index];
            if (record === undefined)
                return invalidEncode("snapshot record directory");
            const directoryOffset = SNAPSHOT_HEADER_BYTES + index * DIRECTORY_ENTRY_BYTES;
            view.setUint8(directoryOffset, record.subchunkY);
            view.setUint32(directoryOffset + 4, recordOffset, true);
            view.setUint32(directoryOffset + 8, record.bytes.byteLength, true);
            view.setUint32(directoryOffset + 12, record.crc32c, true);
            bytes.set(record.bytes, recordOffset);
            recordOffset += record.bytes.byteLength;
        }
        if (recordOffset !== bodyEndOffset)
            return invalidEncode("snapshot record packing");
        const containerCrc = checkedCrc32c(checksumProvider, bytes.subarray(0, bodyEndOffset));
        if (containerCrc === null)
            return invalidEncode("CRC32C provider result");
        view.setUint32(bodyEndOffset, containerCrc, true);
        const digest = await checkedSha256(checksumProvider, bytes.subarray(0, bodyEndOffset + 4));
        if (digest === null)
            return invalidEncode("SHA-256 provider result");
        bytes.set(digest, bodyEndOffset + 4);
        return bytes;
    }
    async function decodeSnapshotCommon(bytes, magic, identityChecker, revisionChecker) {
        const input = copyDecodeInput(bytes, SNAPSHOT_MAX_BYTES);
        if (input === null || input.byteLength < SNAPSHOT_HEADER_BYTES + SNAPSHOT_TRAILER_BYTES) {
            return decodeFailure("INVALID_LENGTH", null);
        }
        const view = new DataView(input.buffer);
        if (!hasMagic(input, magic))
            return decodeFailure("INVALID_HEADER", 0);
        if (view.getUint16(4, true) !== WORLD_FORMAT_V1.worldFormatVersion
            || view.getUint16(6, true) !== WORLD_FORMAT_V1.generatorVersion
            || view.getUint16(8, true) !== WORLD_FORMAT_V1.blockCatalogVersion) {
            return decodeFailure("UNSUPPORTED_VERSION", 4);
        }
        if (view.getUint16(10, true) !== SNAPSHOT_HEADER_BYTES) {
            return decodeFailure("INVALID_HEADER", 10);
        }
        if (view.getUint16(12, true) !== 0 || view.getUint32(60, true) !== 0) {
            return decodeFailure("INVALID_HEADER", view.getUint16(12, true) !== 0 ? 12 : 60);
        }
        const recordCount = view.getUint8(14);
        if (recordCount > MAX_SUBCHUNK_RECORDS)
            return decodeFailure("INVALID_COUNT", 14);
        if (view.getUint8(15) !== DIRECTORY_ENTRY_BYTES) {
            return decodeFailure("INVALID_HEADER", 15);
        }
        const directoryOffset = view.getUint32(48, true);
        const recordsOffset = view.getUint32(52, true);
        const bodyEndOffset = view.getUint32(56, true);
        const expectedRecordsOffset = SNAPSHOT_HEADER_BYTES + recordCount * DIRECTORY_ENTRY_BYTES;
        if (directoryOffset !== SNAPSHOT_HEADER_BYTES)
            return decodeFailure("INVALID_OFFSET", 48);
        if (recordsOffset !== expectedRecordsOffset)
            return decodeFailure("INVALID_OFFSET", 52);
        if (bodyEndOffset < recordsOffset
            || bodyEndOffset + SNAPSHOT_TRAILER_BYTES !== input.byteLength) {
            return decodeFailure(bodyEndOffset + SNAPSHOT_TRAILER_BYTES < input.byteLength
                ? "TRAILING_BYTES"
                : "INVALID_LENGTH", 56);
        }
        const storedContainerCrc = view.getUint32(bodyEndOffset, true);
        const actualContainerCrc = checkedCrc32c(checksumProvider, input.subarray(0, bodyEndOffset));
        if (actualContainerCrc === null || actualContainerCrc !== storedContainerCrc) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset);
        }
        const actualDigest = await checkedSha256(checksumProvider, input.subarray(0, bodyEndOffset + 4));
        if (actualDigest === null
            || !equalBytes(actualDigest, input.subarray(bodyEndOffset + 4))) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset + 4);
        }
        const identity = identityChecker(input.slice(16, 32));
        if (!identity.ok)
            return decodeFailure("INVALID_HEADER", 16);
        const chunkX = checkedValues.int32(view.getInt32(32, true));
        const chunkZ = checkedValues.int32(view.getInt32(36, true));
        if (!chunkX.ok || !chunkZ.ok)
            return decodeFailure("INVALID_HEADER", 32);
        if (chunkX.value < WORLD_FORMAT_V1.worldMinChunkX
            || chunkX.value > WORLD_FORMAT_V1.worldMaxChunkX
            || chunkZ.value < WORLD_FORMAT_V1.worldMinChunkZ
            || chunkZ.value > WORLD_FORMAT_V1.worldMaxChunkZ) {
            return decodeFailure("INVALID_COUNT", 32);
        }
        const throughRevision = revisionChecker(view.getBigUint64(40, true));
        if (!throughRevision.ok)
            return decodeFailure("INVALID_REVISION", 40);
        const records = [];
        let expectedRecordOffset = recordsOffset;
        let previousSubchunkY = -1;
        for (let index = 0; index < recordCount; index += 1) {
            const entryOffset = SNAPSHOT_HEADER_BYTES + index * DIRECTORY_ENTRY_BYTES;
            const subchunkY = view.getUint8(entryOffset);
            if (view.getUint8(entryOffset + 1) !== 0
                || view.getUint8(entryOffset + 2) !== 0
                || view.getUint8(entryOffset + 3) !== 0) {
                return decodeFailure("INVALID_HEADER", entryOffset + 1);
            }
            if (subchunkY <= previousSubchunkY || subchunkY > 7) {
                return decodeFailure("INVALID_ORDER", entryOffset);
            }
            const recordOffset = view.getUint32(entryOffset + 4, true);
            const recordLength = view.getUint32(entryOffset + 8, true);
            const copiedRecordCrc = view.getUint32(entryOffset + 12, true);
            if (recordOffset !== expectedRecordOffset) {
                return decodeFailure("INVALID_OFFSET", entryOffset + 4);
            }
            if (recordLength < MODIFIED_RECORD_MIN_BYTES
                || recordLength > MODIFIED_RECORD_MAX_BYTES
                || recordOffset > bodyEndOffset
                || recordLength > bodyEndOffset - recordOffset) {
                return decodeFailure("INVALID_LENGTH", entryOffset + 8);
            }
            if (view.getUint32(recordOffset + recordLength - 4, true) !== copiedRecordCrc) {
                return decodeFailure("CHECKSUM_MISMATCH", entryOffset + 12);
            }
            const decoded = parseRecord(input.slice(recordOffset, recordOffset + recordLength), revisionChecker);
            if (!decoded.ok) {
                const nestedOffset = decoded.error.byteOffset;
                return decodeFailure(decoded.error.code, nestedOffset === null ? recordOffset : recordOffset + nestedOffset);
            }
            if (decoded.value.subchunkY !== subchunkY) {
                return decodeFailure("INVALID_ORDER", entryOffset);
            }
            if (decoded.value.revision > throughRevision.value) {
                return decodeFailure("INVALID_REVISION", recordOffset + 8);
            }
            records.push(decoded.value);
            expectedRecordOffset += recordLength;
            previousSubchunkY = subchunkY;
        }
        if (expectedRecordOffset !== bodyEndOffset) {
            return decodeFailure("INVALID_OFFSET", 56);
        }
        return decodeSuccess(Object.freeze({
            identity: identity.value,
            chunkX: chunkX.value,
            chunkZ: chunkZ.value,
            throughRevision: throughRevision.value,
            records: Object.freeze(records),
        }));
    }
    async function encodeChunkSnapshot(value) {
        if (typeof value !== "object" || value === null || value.scope !== "shared_world"
            || value.worldFormatVersion !== 1 || value.generatorVersion !== 1
            || value.blockCatalogVersion !== 1) {
            return invalidEncode("shared chunk snapshot");
        }
        const worldId = requireChecked(checkedValues.sharedWorldUuid(value.worldId), "worldId");
        const throughRevision = requireChecked(checkedValues.worldRevision(value.throughRevision), "throughRevision");
        return encodeSnapshotCommon(MAGIC_HWCS, worldId, value.chunkX, value.chunkZ, throughRevision, value.records);
    }
    async function decodeChunkSnapshot(bytes) {
        const decoded = await decodeSnapshotCommon(bytes, MAGIC_HWCS, (value) => checkedValues.sharedWorldUuid(value), (value) => checkedValues.worldRevision(value));
        if (!decoded.ok)
            return decoded;
        return decodeSuccess(Object.freeze({
            scope: "shared_world",
            worldId: decoded.value.identity,
            generatorVersion: 1,
            blockCatalogVersion: 1,
            worldFormatVersion: 1,
            chunkX: decoded.value.chunkX,
            chunkZ: decoded.value.chunkZ,
            throughRevision: decoded.value.throughRevision,
            records: decoded.value.records,
        }));
    }
    async function encodeLocalSandboxChunkSnapshot(value) {
        if (typeof value !== "object" || value === null || value.scope !== "local_sandbox"
            || value.worldFormatVersion !== 1 || value.generatorVersion !== 1
            || value.blockCatalogVersion !== 1) {
            return invalidEncode("local chunk snapshot");
        }
        const sandboxId = requireChecked(checkedValues.localSandboxUuid(value.sandboxId), "sandboxId");
        const throughRevision = requireChecked(checkedValues.localSandboxRevision(value.throughRevision), "local throughRevision");
        return encodeSnapshotCommon(MAGIC_HWLS, sandboxId, value.chunkX, value.chunkZ, throughRevision, value.records);
    }
    async function decodeLocalSandboxChunkSnapshot(bytes) {
        const decoded = await decodeSnapshotCommon(bytes, MAGIC_HWLS, (value) => checkedValues.localSandboxUuid(value), (value) => checkedValues.localSandboxRevision(value));
        if (!decoded.ok)
            return decoded;
        return decodeSuccess(Object.freeze({
            scope: "local_sandbox",
            sandboxId: decoded.value.identity,
            generatorVersion: 1,
            blockCatalogVersion: 1,
            worldFormatVersion: 1,
            chunkX: decoded.value.chunkX,
            chunkZ: decoded.value.chunkZ,
            throughRevision: decoded.value.throughRevision,
            records: decoded.value.records,
        }));
    }
    function encodeDeltaCommon(magic, headerBytes, chunkOffset, baseRevisionOffset, throughRevisionOffset, chunkXValue, chunkZValue, baseRevisionValue, throughRevisionValue, records, identity) {
        const chunkX = requireChecked(checkedValues.int32(chunkXValue), "delta chunkX");
        const chunkZ = requireChecked(checkedValues.int32(chunkZValue), "delta chunkZ");
        if (chunkX < WORLD_FORMAT_V1.worldMinChunkX
            || chunkX > WORLD_FORMAT_V1.worldMaxChunkX
            || chunkZ < WORLD_FORMAT_V1.worldMinChunkZ
            || chunkZ > WORLD_FORMAT_V1.worldMaxChunkZ) {
            return invalidEncode("delta chunk bounds");
        }
        const baseRevision = requireChecked(checkedValues.uint64(baseRevisionValue), "delta base revision");
        const throughRevision = requireChecked(checkedValues.uint64(throughRevisionValue), "delta through revision");
        if (throughRevision < baseRevision)
            return invalidEncode("delta revision range");
        const encodedRecords = validateRecordListForEncoding(records, baseRevision, throughRevision);
        let recordsLength = 0;
        for (const record of encodedRecords)
            recordsLength += record.bytes.byteLength;
        const recordsOffset = headerBytes + encodedRecords.length * DIRECTORY_ENTRY_BYTES;
        const bodyEndOffset = recordsOffset + recordsLength;
        const totalLength = bodyEndOffset + DELTA_TRAILER_BYTES;
        if (totalLength > DELTA_MAX_BYTES)
            return invalidEncode("chunk delta length");
        const bytes = new Uint8Array(totalLength);
        const view = new DataView(bytes.buffer);
        writeMagic(bytes, magic);
        view.setUint16(4, WORLD_FORMAT_V1.worldFormatVersion, true);
        view.setUint8(6, 1);
        view.setUint8(7, encodedRecords.length);
        if (identity !== null) {
            const identityCopy = copyUint8ArrayOfExactLength(identity, 16);
            if (identityCopy === null || chunkOffset < 24)
                return invalidEncode("delta identity");
            bytes.set(identityCopy, 8);
        }
        view.setInt32(chunkOffset, chunkX, true);
        view.setInt32(chunkOffset + 4, chunkZ, true);
        view.setBigUint64(baseRevisionOffset, baseRevision, true);
        view.setBigUint64(throughRevisionOffset, throughRevision, true);
        view.setUint32(headerBytes - 8, headerBytes, true);
        view.setUint32(headerBytes - 4, bodyEndOffset, true);
        let recordOffset = recordsOffset;
        for (let index = 0; index < encodedRecords.length; index += 1) {
            const record = encodedRecords[index];
            if (record === undefined)
                return invalidEncode("delta record directory");
            const entryOffset = headerBytes + index * DIRECTORY_ENTRY_BYTES;
            view.setUint8(entryOffset, record.subchunkY);
            view.setUint32(entryOffset + 4, recordOffset, true);
            view.setUint32(entryOffset + 8, record.bytes.byteLength, true);
            view.setUint32(entryOffset + 12, record.crc32c, true);
            bytes.set(record.bytes, recordOffset);
            recordOffset += record.bytes.byteLength;
        }
        if (recordOffset !== bodyEndOffset)
            return invalidEncode("delta record packing");
        const crc = checkedCrc32c(checksumProvider, bytes.subarray(0, bodyEndOffset));
        if (crc === null)
            return invalidEncode("CRC32C provider result");
        view.setUint32(bodyEndOffset, crc, true);
        return bytes;
    }
    function decodeDeltaCommon(bytes, magic, headerBytes, chunkOffset, baseRevisionOffset, throughRevisionOffset, revisionChecker) {
        const input = copyDecodeInput(bytes, DELTA_MAX_BYTES);
        if (input === null || input.byteLength < headerBytes + DELTA_TRAILER_BYTES) {
            return decodeFailure("INVALID_LENGTH", null);
        }
        const view = new DataView(input.buffer);
        if (!hasMagic(input, magic))
            return decodeFailure("INVALID_HEADER", 0);
        if (view.getUint16(4, true) !== WORLD_FORMAT_V1.worldFormatVersion) {
            return decodeFailure("UNSUPPORTED_VERSION", 4);
        }
        if (view.getUint8(6) !== 1)
            return decodeFailure("UNSUPPORTED_VERSION", 6);
        const recordCount = view.getUint8(7);
        if (recordCount > MAX_SUBCHUNK_RECORDS)
            return decodeFailure("INVALID_COUNT", 7);
        const directoryOffset = view.getUint32(headerBytes - 8, true);
        const bodyEndOffset = view.getUint32(headerBytes - 4, true);
        const recordsOffset = headerBytes + recordCount * DIRECTORY_ENTRY_BYTES;
        if (directoryOffset !== headerBytes) {
            return decodeFailure("INVALID_OFFSET", headerBytes - 8);
        }
        if (bodyEndOffset < recordsOffset || bodyEndOffset + 4 !== input.byteLength) {
            return decodeFailure(bodyEndOffset + 4 < input.byteLength ? "TRAILING_BYTES" : "INVALID_LENGTH", headerBytes - 4);
        }
        const storedCrc = view.getUint32(bodyEndOffset, true);
        const actualCrc = checkedCrc32c(checksumProvider, input.subarray(0, bodyEndOffset));
        if (actualCrc === null || storedCrc !== actualCrc) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset);
        }
        const chunkX = checkedValues.int32(view.getInt32(chunkOffset, true));
        const chunkZ = checkedValues.int32(view.getInt32(chunkOffset + 4, true));
        if (!chunkX.ok || !chunkZ.ok)
            return decodeFailure("INVALID_HEADER", chunkOffset);
        if (chunkX.value < WORLD_FORMAT_V1.worldMinChunkX
            || chunkX.value > WORLD_FORMAT_V1.worldMaxChunkX
            || chunkZ.value < WORLD_FORMAT_V1.worldMinChunkZ
            || chunkZ.value > WORLD_FORMAT_V1.worldMaxChunkZ) {
            return decodeFailure("INVALID_COUNT", chunkOffset);
        }
        const baseRevision = revisionChecker(view.getBigUint64(baseRevisionOffset, true));
        const throughRevision = revisionChecker(view.getBigUint64(throughRevisionOffset, true));
        if (!baseRevision.ok)
            return decodeFailure("INVALID_REVISION", baseRevisionOffset);
        if (!throughRevision.ok || throughRevision.value < baseRevision.value) {
            return decodeFailure("INVALID_REVISION", throughRevisionOffset);
        }
        const records = [];
        let expectedRecordOffset = recordsOffset;
        let previousSubchunkY = -1;
        for (let index = 0; index < recordCount; index += 1) {
            const entryOffset = headerBytes + index * DIRECTORY_ENTRY_BYTES;
            const subchunkY = view.getUint8(entryOffset);
            if (view.getUint8(entryOffset + 1) !== 0
                || view.getUint8(entryOffset + 2) !== 0
                || view.getUint8(entryOffset + 3) !== 0) {
                return decodeFailure("INVALID_HEADER", entryOffset + 1);
            }
            if (subchunkY <= previousSubchunkY || subchunkY > 7) {
                return decodeFailure("INVALID_ORDER", entryOffset);
            }
            const recordOffset = view.getUint32(entryOffset + 4, true);
            const recordLength = view.getUint32(entryOffset + 8, true);
            const copiedRecordCrc = view.getUint32(entryOffset + 12, true);
            if (recordOffset !== expectedRecordOffset) {
                return decodeFailure("INVALID_OFFSET", entryOffset + 4);
            }
            if (recordLength < MODIFIED_RECORD_MIN_BYTES
                || recordLength > MODIFIED_RECORD_MAX_BYTES
                || recordOffset > bodyEndOffset
                || recordLength > bodyEndOffset - recordOffset) {
                return decodeFailure("INVALID_LENGTH", entryOffset + 8);
            }
            if (view.getUint32(recordOffset + recordLength - 4, true) !== copiedRecordCrc) {
                return decodeFailure("CHECKSUM_MISMATCH", entryOffset + 12);
            }
            const decoded = parseRecord(input.slice(recordOffset, recordOffset + recordLength), revisionChecker);
            if (!decoded.ok) {
                const nestedOffset = decoded.error.byteOffset;
                return decodeFailure(decoded.error.code, nestedOffset === null ? recordOffset : recordOffset + nestedOffset);
            }
            if (decoded.value.subchunkY !== subchunkY) {
                return decodeFailure("INVALID_ORDER", entryOffset);
            }
            if (decoded.value.revision < baseRevision.value
                || decoded.value.revision > throughRevision.value) {
                return decodeFailure("INVALID_REVISION", recordOffset + 8);
            }
            records.push(decoded.value);
            expectedRecordOffset += recordLength;
            previousSubchunkY = subchunkY;
        }
        if (expectedRecordOffset !== bodyEndOffset) {
            return decodeFailure("INVALID_OFFSET", headerBytes - 4);
        }
        return decodeSuccess(Object.freeze({
            identityBytes: chunkOffset >= 24 ? input.slice(8, 24) : new Uint8Array(0),
            chunkX: chunkX.value,
            chunkZ: chunkZ.value,
            baseRevision: baseRevision.value,
            throughRevision: throughRevision.value,
            records: Object.freeze(records),
        }));
    }
    function encodeChunkDelta(value) {
        if (typeof value !== "object" || value === null || value.scope !== "shared_world"
            || value.codecId !== 1) {
            return invalidEncode("shared chunk delta");
        }
        const baseRevision = requireChecked(checkedValues.worldRevision(value.baseSnapshotRevision), "baseSnapshotRevision");
        const throughRevision = requireChecked(checkedValues.worldRevision(value.throughRevision), "throughRevision");
        return encodeDeltaCommon(MAGIC_HWCD, DELTA_SHARED_HEADER_BYTES, 8, 16, 24, value.chunkX, value.chunkZ, baseRevision, throughRevision, value.records, null);
    }
    function decodeChunkDelta(bytes) {
        const decoded = decodeDeltaCommon(bytes, MAGIC_HWCD, DELTA_SHARED_HEADER_BYTES, 8, 16, 24, (value) => checkedValues.worldRevision(value));
        if (!decoded.ok)
            return decoded;
        return decodeSuccess(Object.freeze({
            scope: "shared_world",
            codecId: 1,
            chunkX: decoded.value.chunkX,
            chunkZ: decoded.value.chunkZ,
            baseSnapshotRevision: decoded.value.baseRevision,
            throughRevision: decoded.value.throughRevision,
            records: decoded.value.records,
        }));
    }
    function encodeLocalSandboxChunkDelta(value) {
        if (typeof value !== "object" || value === null || value.scope !== "local_sandbox"
            || value.codecId !== 1) {
            return invalidEncode("local chunk delta");
        }
        const sandboxId = requireChecked(checkedValues.localSandboxUuid(value.sandboxId), "sandboxId");
        const baseRevision = requireChecked(checkedValues.localSandboxRevision(value.baseLocalRevision), "baseLocalRevision");
        const throughRevision = requireChecked(checkedValues.localSandboxRevision(value.throughLocalRevision), "throughLocalRevision");
        return encodeDeltaCommon(MAGIC_HWLD, DELTA_LOCAL_HEADER_BYTES, 24, 32, 40, value.chunkX, value.chunkZ, baseRevision, throughRevision, value.records, sandboxId);
    }
    function decodeLocalSandboxChunkDelta(bytes) {
        const decoded = decodeDeltaCommon(bytes, MAGIC_HWLD, DELTA_LOCAL_HEADER_BYTES, 24, 32, 40, (value) => checkedValues.localSandboxRevision(value));
        if (!decoded.ok)
            return decoded;
        const sandboxId = checkedValues.localSandboxUuid(decoded.value.identityBytes);
        if (!sandboxId.ok)
            return decodeFailure("INVALID_HEADER", 8);
        return decodeSuccess(Object.freeze({
            scope: "local_sandbox",
            sandboxId: sandboxId.value,
            codecId: 1,
            chunkX: decoded.value.chunkX,
            chunkZ: decoded.value.chunkZ,
            baseLocalRevision: decoded.value.baseRevision,
            throughLocalRevision: decoded.value.throughRevision,
            records: decoded.value.records,
        }));
    }
    function validateSharedMetadataForEncoding(metadata, snapshotRevisionValue) {
        if (typeof metadata !== "object" || metadata === null || metadata.scope !== "shared_world"
            || metadata.generatorVersion !== 1 || metadata.blockCatalogVersion !== 1
            || metadata.worldFormatVersion !== 1
            || metadata.minChunkX !== WORLD_FORMAT_V1.worldMinChunkX
            || metadata.maxChunkX !== WORLD_FORMAT_V1.worldMaxChunkX
            || metadata.minChunkZ !== WORLD_FORMAT_V1.worldMinChunkZ
            || metadata.maxChunkZ !== WORLD_FORMAT_V1.worldMaxChunkZ
            || metadata.worldHeight !== WORLD_FORMAT_V1.worldHeight
            || metadata.seaLevel !== WORLD_FORMAT_V1.seaLevel) {
            return invalidEncode("shared manifest metadata");
        }
        const worldId = requireChecked(checkedValues.sharedWorldUuid(metadata.worldId), "worldId");
        const seed = requireChecked(checkedValues.worldSeed(metadata.seed), "world seed");
        const latestRevision = requireChecked(checkedValues.worldRevision(metadata.latestRevision), "latestRevision");
        const snapshotRevision = requireChecked(checkedValues.worldRevision(snapshotRevisionValue), "snapshotRevision");
        if (latestRevision !== snapshotRevision)
            return invalidEncode("manifest revision binding");
        const spawnXmm = requireChecked(checkedValues.int32(metadata.spawnXmm), "spawnXmm");
        const spawnYmm = requireChecked(checkedValues.int32(metadata.spawnYmm), "spawnYmm");
        const spawnZmm = requireChecked(checkedValues.int32(metadata.spawnZmm), "spawnZmm");
        const canonical = checkedValues.createSharedMetadata({
            worldId,
            seed,
            latestRevision,
            spawnXmm,
            spawnYmm,
            spawnZmm,
        });
        if (!canonical.ok
            || !isUint8Array(metadata.biomeConfiguration)
            || !isUint8Array(metadata.biomeConfigHash)
            || !isUint8Array(metadata.blockCatalogHash)
            || !equalBytes(metadata.biomeConfiguration, canonical.value.biomeConfiguration)
            || !equalBytes(metadata.biomeConfigHash, canonical.value.biomeConfigHash)
            || !equalBytes(metadata.blockCatalogHash, canonical.value.blockCatalogHash)) {
            return invalidEncode("canonical shared manifest metadata");
        }
        return canonical.value;
    }
    function validateLocalMetadataForEncoding(metadata, localRevisionValue) {
        if (typeof metadata !== "object" || metadata === null || metadata.scope !== "local_sandbox"
            || metadata.generatorVersion !== 1 || metadata.blockCatalogVersion !== 1
            || metadata.worldFormatVersion !== 1
            || metadata.minChunkX !== WORLD_FORMAT_V1.worldMinChunkX
            || metadata.maxChunkX !== WORLD_FORMAT_V1.worldMaxChunkX
            || metadata.minChunkZ !== WORLD_FORMAT_V1.worldMinChunkZ
            || metadata.maxChunkZ !== WORLD_FORMAT_V1.worldMaxChunkZ
            || metadata.worldHeight !== WORLD_FORMAT_V1.worldHeight
            || metadata.seaLevel !== WORLD_FORMAT_V1.seaLevel) {
            return invalidEncode("local manifest metadata");
        }
        const sandboxId = requireChecked(checkedValues.localSandboxUuid(metadata.sandboxId), "sandboxId");
        const seed = requireChecked(checkedValues.worldSeed(metadata.seed), "world seed");
        const localRevision = requireChecked(checkedValues.localSandboxRevision(metadata.localRevision), "localRevision");
        const manifestRevision = requireChecked(checkedValues.localSandboxRevision(localRevisionValue), "manifest localRevision");
        const manifestGeneration = requireChecked(checkedValues.uint64(metadata.manifestGeneration), "manifestGeneration");
        if (localRevision !== manifestRevision || manifestGeneration === 0n) {
            return invalidEncode("local manifest revision/generation binding");
        }
        const spawnXmm = requireChecked(checkedValues.int32(metadata.spawnXmm), "spawnXmm");
        const spawnYmm = requireChecked(checkedValues.int32(metadata.spawnYmm), "spawnYmm");
        const spawnZmm = requireChecked(checkedValues.int32(metadata.spawnZmm), "spawnZmm");
        const canonical = checkedValues.createLocalSandboxMetadata({
            sandboxId,
            seed,
            localRevision,
            manifestGeneration,
            spawnXmm,
            spawnYmm,
            spawnZmm,
        });
        if (!canonical.ok
            || !isUint8Array(metadata.biomeConfiguration)
            || !isUint8Array(metadata.biomeConfigHash)
            || !isUint8Array(metadata.blockCatalogHash)
            || !equalBytes(metadata.biomeConfiguration, canonical.value.biomeConfiguration)
            || !equalBytes(metadata.biomeConfigHash, canonical.value.biomeConfigHash)
            || !equalBytes(metadata.blockCatalogHash, canonical.value.blockCatalogHash)) {
            return invalidEncode("canonical local manifest metadata");
        }
        return canonical.value;
    }
    function prepareSharedManifestEntries(chunks, snapshotRevision) {
        if (!Array.isArray(chunks) || chunks.length > MAX_MANIFEST_ENTRIES) {
            return invalidEncode("shared manifest entry list");
        }
        const prepared = [];
        let previous;
        for (const entry of chunks) {
            if (typeof entry !== "object" || entry === null) {
                return invalidEncode("shared manifest entry");
            }
            const chunkX = requireChecked(checkedValues.int32(entry.chunkX), "chunkX");
            const chunkZ = requireChecked(checkedValues.int32(entry.chunkZ), "chunkZ");
            const entryRevision = requireChecked(checkedValues.worldRevision(entry.snapshotRevision), "entry snapshotRevision");
            const chunkByteLength = requireChecked(checkedValues.uint32(entry.chunkByteLength), "chunkByteLength");
            const chunkContainerCrc32c = requireChecked(checkedValues.uint32(entry.chunkContainerCrc32c), "chunkContainerCrc32c");
            const chunkSha256 = requireChecked(checkedValues.sha256Digest(entry.chunkSha256), "chunkSha256");
            const objectKeyDigestPrefix = requireChecked(checkedValues.sha256Prefix24(entry.objectKeyDigestPrefix), "objectKeyDigestPrefix");
            const current = Object.freeze({
                chunkX,
                chunkZ,
                snapshotRevision: entryRevision,
                chunkByteLength,
                chunkContainerCrc32c,
                chunkSha256,
                objectKeyDigestPrefix,
            });
            if (chunkX < WORLD_FORMAT_V1.worldMinChunkX
                || chunkX > WORLD_FORMAT_V1.worldMaxChunkX
                || chunkZ < WORLD_FORMAT_V1.worldMinChunkZ
                || chunkZ > WORLD_FORMAT_V1.worldMaxChunkZ
                || chunkByteLength < SNAPSHOT_HEADER_BYTES + SNAPSHOT_TRAILER_BYTES
                || chunkByteLength > SNAPSHOT_MAX_BYTES
                || entryRevision > snapshotRevision
                || (previous !== undefined && compareChunkOrder(previous, current) >= 0)) {
                return invalidEncode("ordered shared manifest entry");
            }
            prepared.push(current);
            previous = current;
        }
        return Object.freeze(prepared);
    }
    function prepareLocalManifestEntries(chunks, localRevision) {
        if (!Array.isArray(chunks) || chunks.length > MAX_MANIFEST_ENTRIES) {
            return invalidEncode("local manifest entry list");
        }
        const prepared = [];
        let previous;
        for (const entry of chunks) {
            if (typeof entry !== "object" || entry === null) {
                return invalidEncode("local manifest entry");
            }
            const chunkX = requireChecked(checkedValues.int32(entry.chunkX), "chunkX");
            const chunkZ = requireChecked(checkedValues.int32(entry.chunkZ), "chunkZ");
            const entryRevision = requireChecked(checkedValues.localSandboxRevision(entry.localRevision), "entry localRevision");
            const chunkByteLength = requireChecked(checkedValues.uint32(entry.chunkByteLength), "chunkByteLength");
            const chunkContainerCrc32c = requireChecked(checkedValues.uint32(entry.chunkContainerCrc32c), "chunkContainerCrc32c");
            const chunkSha256 = requireChecked(checkedValues.sha256Digest(entry.chunkSha256), "chunkSha256");
            const current = Object.freeze({
                chunkX,
                chunkZ,
                localRevision: entryRevision,
                chunkByteLength,
                chunkContainerCrc32c,
                chunkSha256,
            });
            if (chunkX < WORLD_FORMAT_V1.worldMinChunkX
                || chunkX > WORLD_FORMAT_V1.worldMaxChunkX
                || chunkZ < WORLD_FORMAT_V1.worldMinChunkZ
                || chunkZ > WORLD_FORMAT_V1.worldMaxChunkZ
                || chunkByteLength < SNAPSHOT_HEADER_BYTES + SNAPSHOT_TRAILER_BYTES
                || chunkByteLength > SNAPSHOT_MAX_BYTES
                || entryRevision > localRevision
                || (previous !== undefined && compareChunkOrder(previous, current) >= 0)) {
                return invalidEncode("ordered local manifest entry");
            }
            prepared.push(current);
            previous = current;
        }
        return Object.freeze(prepared);
    }
    async function objectKeyDigestPrefix(worldId, chunkX, chunkZ, chunkSha256) {
        const bytes = new Uint8Array(OBJECT_KEY_DOMAIN.byteLength + 16 + 4 + 4 + 32);
        const view = new DataView(bytes.buffer);
        bytes.set(OBJECT_KEY_DOMAIN, 0);
        bytes.set(worldId, OBJECT_KEY_DOMAIN.byteLength);
        const coordinateOffset = OBJECT_KEY_DOMAIN.byteLength + 16;
        view.setInt32(coordinateOffset, chunkX, true);
        view.setInt32(coordinateOffset + 4, chunkZ, true);
        bytes.set(chunkSha256, coordinateOffset + 8);
        const digest = await checkedSha256(checksumProvider, bytes);
        if (digest === null)
            return null;
        const prefix = checkedValues.sha256Prefix24(digest.slice(0, 24));
        return prefix.ok ? prefix.value : null;
    }
    async function encodeWorldSnapshotManifest(value) {
        if (typeof value !== "object" || value === null || value.scope !== "shared_world") {
            return invalidEncode("shared world manifest");
        }
        const snapshotRevision = requireChecked(checkedValues.worldRevision(value.snapshotRevision), "snapshotRevision");
        const metadata = validateSharedMetadataForEncoding(value.metadata, snapshotRevision);
        const entries = prepareSharedManifestEntries(value.chunks, snapshotRevision);
        for (const entry of entries) {
            const expectedPrefix = await objectKeyDigestPrefix(metadata.worldId, entry.chunkX, entry.chunkZ, entry.chunkSha256);
            if (expectedPrefix === null
                || !equalBytes(expectedPrefix, entry.objectKeyDigestPrefix)) {
                return invalidEncode("shared manifest object-key digest prefix");
            }
        }
        const bodyEndOffset = SHARED_MANIFEST_HEADER_BYTES
            + entries.length * SHARED_MANIFEST_ENTRY_BYTES;
        const bytes = new Uint8Array(bodyEndOffset + MANIFEST_TRAILER_BYTES);
        const view = new DataView(bytes.buffer);
        writeMagic(bytes, MAGIC_HWMS);
        view.setUint16(4, 1, true);
        view.setUint16(6, 1, true);
        view.setUint16(8, 1, true);
        view.setUint16(10, SHARED_MANIFEST_HEADER_BYTES, true);
        view.setUint32(12, 0, true);
        bytes.set(metadata.worldId, 16);
        view.setBigUint64(32, metadata.seed, true);
        view.setBigUint64(40, snapshotRevision, true);
        view.setInt32(48, metadata.minChunkX, true);
        view.setInt32(52, metadata.maxChunkX, true);
        view.setInt32(56, metadata.minChunkZ, true);
        view.setInt32(60, metadata.maxChunkZ, true);
        view.setInt32(64, metadata.spawnXmm, true);
        view.setInt32(68, metadata.spawnYmm, true);
        view.setInt32(72, metadata.spawnZmm, true);
        view.setUint16(76, metadata.worldHeight, true);
        view.setUint16(78, metadata.seaLevel, true);
        view.setUint16(80, metadata.biomeConfiguration.byteLength, true);
        view.setUint16(82, SHARED_MANIFEST_ENTRY_BYTES, true);
        view.setUint32(84, entries.length, true);
        view.setUint32(88, SHARED_MANIFEST_HEADER_BYTES, true);
        view.setUint32(92, bodyEndOffset, true);
        bytes.set(metadata.biomeConfigHash, 96);
        bytes.set(metadata.blockCatalogHash, 128);
        bytes.set(metadata.biomeConfiguration, 160);
        for (let index = 0; index < entries.length; index += 1) {
            const entry = entries[index];
            if (entry === undefined)
                return invalidEncode("shared manifest entry packing");
            const offset = SHARED_MANIFEST_HEADER_BYTES + index * SHARED_MANIFEST_ENTRY_BYTES;
            view.setInt32(offset, entry.chunkX, true);
            view.setInt32(offset + 4, entry.chunkZ, true);
            view.setBigUint64(offset + 8, entry.snapshotRevision, true);
            view.setUint32(offset + 16, entry.chunkByteLength, true);
            view.setUint32(offset + 20, entry.chunkContainerCrc32c, true);
            bytes.set(entry.chunkSha256, offset + 24);
            bytes.set(entry.objectKeyDigestPrefix, offset + 56);
        }
        const crc = checkedCrc32c(checksumProvider, bytes.subarray(0, bodyEndOffset));
        if (crc === null)
            return invalidEncode("CRC32C provider result");
        view.setUint32(bodyEndOffset, crc, true);
        const digest = await checkedSha256(checksumProvider, bytes.subarray(0, bodyEndOffset + 4));
        if (digest === null)
            return invalidEncode("SHA-256 provider result");
        bytes.set(digest, bodyEndOffset + 4);
        return bytes;
    }
    async function encodeLocalSandboxManifest(value) {
        if (typeof value !== "object" || value === null || value.scope !== "local_sandbox") {
            return invalidEncode("local sandbox manifest");
        }
        const localRevision = requireChecked(checkedValues.localSandboxRevision(value.localRevision), "localRevision");
        const metadata = validateLocalMetadataForEncoding(value.metadata, localRevision);
        const entries = prepareLocalManifestEntries(value.chunks, localRevision);
        const bodyEndOffset = LOCAL_MANIFEST_HEADER_BYTES
            + entries.length * LOCAL_MANIFEST_ENTRY_BYTES;
        const bytes = new Uint8Array(bodyEndOffset + MANIFEST_TRAILER_BYTES);
        const view = new DataView(bytes.buffer);
        writeMagic(bytes, MAGIC_HWLM);
        view.setUint16(4, 1, true);
        view.setUint16(6, 1, true);
        view.setUint16(8, 1, true);
        view.setUint16(10, LOCAL_MANIFEST_HEADER_BYTES, true);
        view.setUint32(12, 0, true);
        bytes.set(metadata.sandboxId, 16);
        view.setBigUint64(32, metadata.seed, true);
        view.setBigUint64(40, localRevision, true);
        view.setBigUint64(48, metadata.manifestGeneration, true);
        view.setInt32(56, metadata.minChunkX, true);
        view.setInt32(60, metadata.maxChunkX, true);
        view.setInt32(64, metadata.minChunkZ, true);
        view.setInt32(68, metadata.maxChunkZ, true);
        view.setInt32(72, metadata.spawnXmm, true);
        view.setInt32(76, metadata.spawnYmm, true);
        view.setInt32(80, metadata.spawnZmm, true);
        view.setUint16(84, metadata.worldHeight, true);
        view.setUint16(86, metadata.seaLevel, true);
        view.setUint16(88, metadata.biomeConfiguration.byteLength, true);
        view.setUint16(90, LOCAL_MANIFEST_ENTRY_BYTES, true);
        view.setUint32(92, entries.length, true);
        view.setUint32(96, LOCAL_MANIFEST_HEADER_BYTES, true);
        view.setUint32(100, bodyEndOffset, true);
        bytes.set(metadata.biomeConfigHash, 104);
        bytes.set(metadata.blockCatalogHash, 136);
        bytes.set(metadata.biomeConfiguration, 168);
        for (let index = 0; index < entries.length; index += 1) {
            const entry = entries[index];
            if (entry === undefined)
                return invalidEncode("local manifest entry packing");
            const offset = LOCAL_MANIFEST_HEADER_BYTES + index * LOCAL_MANIFEST_ENTRY_BYTES;
            view.setInt32(offset, entry.chunkX, true);
            view.setInt32(offset + 4, entry.chunkZ, true);
            view.setBigUint64(offset + 8, entry.localRevision, true);
            view.setUint32(offset + 16, entry.chunkByteLength, true);
            view.setUint32(offset + 20, entry.chunkContainerCrc32c, true);
            bytes.set(entry.chunkSha256, offset + 24);
        }
        const crc = checkedCrc32c(checksumProvider, bytes.subarray(0, bodyEndOffset));
        if (crc === null)
            return invalidEncode("CRC32C provider result");
        view.setUint32(bodyEndOffset, crc, true);
        const digest = await checkedSha256(checksumProvider, bytes.subarray(0, bodyEndOffset + 4));
        if (digest === null)
            return invalidEncode("SHA-256 provider result");
        bytes.set(digest, bodyEndOffset + 4);
        return bytes;
    }
    function decodeSharedMetadata(input, view) {
        const worldId = checkedValues.sharedWorldUuid(input.slice(16, 32));
        const seed = checkedValues.worldSeed(view.getBigUint64(32, true));
        const revision = checkedValues.worldRevision(view.getBigUint64(40, true));
        const spawnXmm = checkedValues.int32(view.getInt32(64, true));
        const spawnYmm = checkedValues.int32(view.getInt32(68, true));
        const spawnZmm = checkedValues.int32(view.getInt32(72, true));
        if (!worldId.ok || !seed.ok || !revision.ok
            || !spawnXmm.ok || !spawnYmm.ok || !spawnZmm.ok) {
            return decodeFailure("INVALID_HEADER", 16);
        }
        const metadata = checkedValues.createSharedMetadata({
            worldId: worldId.value,
            seed: seed.value,
            latestRevision: revision.value,
            spawnXmm: spawnXmm.value,
            spawnYmm: spawnYmm.value,
            spawnZmm: spawnZmm.value,
        });
        if (!metadata.ok)
            return decodeFailure("INVALID_HEADER", 64);
        if (view.getInt32(48, true) !== metadata.value.minChunkX
            || view.getInt32(52, true) !== metadata.value.maxChunkX
            || view.getInt32(56, true) !== metadata.value.minChunkZ
            || view.getInt32(60, true) !== metadata.value.maxChunkZ
            || view.getUint16(76, true) !== metadata.value.worldHeight
            || view.getUint16(78, true) !== metadata.value.seaLevel
            || view.getUint16(80, true) !== metadata.value.biomeConfiguration.byteLength
            || !equalBytes(input.subarray(96, 128), metadata.value.biomeConfigHash)
            || !equalBytes(input.subarray(128, 160), metadata.value.blockCatalogHash)
            || !equalBytes(input.subarray(160, 166), metadata.value.biomeConfiguration)) {
            return decodeFailure("INVALID_HEADER", 48);
        }
        return decodeSuccess(metadata.value);
    }
    function decodeLocalMetadata(input, view) {
        const sandboxId = checkedValues.localSandboxUuid(input.slice(16, 32));
        const seed = checkedValues.worldSeed(view.getBigUint64(32, true));
        const revision = checkedValues.localSandboxRevision(view.getBigUint64(40, true));
        const manifestGeneration = checkedValues.uint64(view.getBigUint64(48, true));
        const spawnXmm = checkedValues.int32(view.getInt32(72, true));
        const spawnYmm = checkedValues.int32(view.getInt32(76, true));
        const spawnZmm = checkedValues.int32(view.getInt32(80, true));
        if (!sandboxId.ok || !seed.ok || !revision.ok || !manifestGeneration.ok
            || manifestGeneration.value === 0n
            || !spawnXmm.ok || !spawnYmm.ok || !spawnZmm.ok) {
            return decodeFailure("INVALID_HEADER", 16);
        }
        const metadata = checkedValues.createLocalSandboxMetadata({
            sandboxId: sandboxId.value,
            seed: seed.value,
            localRevision: revision.value,
            manifestGeneration: manifestGeneration.value,
            spawnXmm: spawnXmm.value,
            spawnYmm: spawnYmm.value,
            spawnZmm: spawnZmm.value,
        });
        if (!metadata.ok)
            return decodeFailure("INVALID_HEADER", 72);
        if (view.getInt32(56, true) !== metadata.value.minChunkX
            || view.getInt32(60, true) !== metadata.value.maxChunkX
            || view.getInt32(64, true) !== metadata.value.minChunkZ
            || view.getInt32(68, true) !== metadata.value.maxChunkZ
            || view.getUint16(84, true) !== metadata.value.worldHeight
            || view.getUint16(86, true) !== metadata.value.seaLevel
            || view.getUint16(88, true) !== metadata.value.biomeConfiguration.byteLength
            || !equalBytes(input.subarray(104, 136), metadata.value.biomeConfigHash)
            || !equalBytes(input.subarray(136, 168), metadata.value.blockCatalogHash)
            || !equalBytes(input.subarray(168, 174), metadata.value.biomeConfiguration)) {
            return decodeFailure("INVALID_HEADER", 56);
        }
        return decodeSuccess(metadata.value);
    }
    async function decodeWorldSnapshotManifest(bytes) {
        const input = copyDecodeInput(bytes, MANIFEST_MAX_BYTES);
        if (input === null
            || input.byteLength < SHARED_MANIFEST_HEADER_BYTES + MANIFEST_TRAILER_BYTES) {
            return decodeFailure("INVALID_LENGTH", null);
        }
        const view = new DataView(input.buffer);
        if (!hasMagic(input, MAGIC_HWMS))
            return decodeFailure("INVALID_HEADER", 0);
        if (view.getUint16(4, true) !== 1
            || view.getUint16(6, true) !== 1
            || view.getUint16(8, true) !== 1) {
            return decodeFailure("UNSUPPORTED_VERSION", 4);
        }
        if (view.getUint16(10, true) !== SHARED_MANIFEST_HEADER_BYTES
            || view.getUint32(12, true) !== 0
            || view.getUint16(82, true) !== SHARED_MANIFEST_ENTRY_BYTES) {
            return decodeFailure("INVALID_HEADER", 10);
        }
        const entryCount = view.getUint32(84, true);
        if (entryCount > MAX_MANIFEST_ENTRIES)
            return decodeFailure("INVALID_COUNT", 84);
        const entriesOffset = view.getUint32(88, true);
        const bodyEndOffset = view.getUint32(92, true);
        const expectedBodyEndOffset = SHARED_MANIFEST_HEADER_BYTES
            + entryCount * SHARED_MANIFEST_ENTRY_BYTES;
        if (entriesOffset !== SHARED_MANIFEST_HEADER_BYTES) {
            return decodeFailure("INVALID_OFFSET", 88);
        }
        if (bodyEndOffset !== expectedBodyEndOffset
            || bodyEndOffset + MANIFEST_TRAILER_BYTES !== input.byteLength) {
            return decodeFailure(bodyEndOffset + MANIFEST_TRAILER_BYTES < input.byteLength
                ? "TRAILING_BYTES"
                : "INVALID_LENGTH", 92);
        }
        const storedCrc = view.getUint32(bodyEndOffset, true);
        const actualCrc = checkedCrc32c(checksumProvider, input.subarray(0, bodyEndOffset));
        if (actualCrc === null || actualCrc !== storedCrc) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset);
        }
        const actualDigest = await checkedSha256(checksumProvider, input.subarray(0, bodyEndOffset + 4));
        if (actualDigest === null
            || !equalBytes(actualDigest, input.subarray(bodyEndOffset + 4))) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset + 4);
        }
        const metadata = decodeSharedMetadata(input, view);
        if (!metadata.ok)
            return metadata;
        const snapshotRevision = metadata.value.latestRevision;
        const chunks = [];
        let previous;
        for (let index = 0; index < entryCount; index += 1) {
            const offset = SHARED_MANIFEST_HEADER_BYTES + index * SHARED_MANIFEST_ENTRY_BYTES;
            const chunkX = checkedValues.int32(view.getInt32(offset, true));
            const chunkZ = checkedValues.int32(view.getInt32(offset + 4, true));
            const entryRevision = checkedValues.worldRevision(view.getBigUint64(offset + 8, true));
            const chunkByteLength = checkedValues.uint32(view.getUint32(offset + 16, true));
            const chunkCrc = checkedValues.uint32(view.getUint32(offset + 20, true));
            const chunkSha = checkedValues.sha256Digest(input.slice(offset + 24, offset + 56));
            const objectPrefix = checkedValues.sha256Prefix24(input.slice(offset + 56, offset + 80));
            if (!chunkX.ok || !chunkZ.ok || !entryRevision.ok || !chunkByteLength.ok
                || !chunkCrc.ok || !chunkSha.ok || !objectPrefix.ok) {
                return decodeFailure("INVALID_HEADER", offset);
            }
            const entry = Object.freeze({
                chunkX: chunkX.value,
                chunkZ: chunkZ.value,
                snapshotRevision: entryRevision.value,
                chunkByteLength: chunkByteLength.value,
                chunkContainerCrc32c: chunkCrc.value,
                chunkSha256: chunkSha.value,
                objectKeyDigestPrefix: objectPrefix.value,
            });
            if (entry.chunkX < WORLD_FORMAT_V1.worldMinChunkX
                || entry.chunkX > WORLD_FORMAT_V1.worldMaxChunkX
                || entry.chunkZ < WORLD_FORMAT_V1.worldMinChunkZ
                || entry.chunkZ > WORLD_FORMAT_V1.worldMaxChunkZ
                || entry.chunkByteLength < SNAPSHOT_HEADER_BYTES + SNAPSHOT_TRAILER_BYTES
                || entry.chunkByteLength > SNAPSHOT_MAX_BYTES
                || entry.snapshotRevision > snapshotRevision) {
                return decodeFailure("INVALID_COUNT", offset);
            }
            if (previous !== undefined && compareChunkOrder(previous, entry) >= 0) {
                return decodeFailure("INVALID_ORDER", offset);
            }
            const expectedPrefix = await objectKeyDigestPrefix(metadata.value.worldId, entry.chunkX, entry.chunkZ, entry.chunkSha256);
            if (expectedPrefix === null
                || !equalBytes(expectedPrefix, entry.objectKeyDigestPrefix)) {
                return decodeFailure("CHECKSUM_MISMATCH", offset + 56);
            }
            chunks.push(entry);
            previous = entry;
        }
        return decodeSuccess(Object.freeze({
            scope: "shared_world",
            metadata: metadata.value,
            snapshotRevision,
            chunks: Object.freeze(chunks),
        }));
    }
    async function decodeLocalSandboxManifest(bytes) {
        const input = copyDecodeInput(bytes, MANIFEST_MAX_BYTES);
        if (input === null
            || input.byteLength < LOCAL_MANIFEST_HEADER_BYTES + MANIFEST_TRAILER_BYTES) {
            return decodeFailure("INVALID_LENGTH", null);
        }
        const view = new DataView(input.buffer);
        if (!hasMagic(input, MAGIC_HWLM))
            return decodeFailure("INVALID_HEADER", 0);
        if (view.getUint16(4, true) !== 1
            || view.getUint16(6, true) !== 1
            || view.getUint16(8, true) !== 1) {
            return decodeFailure("UNSUPPORTED_VERSION", 4);
        }
        if (view.getUint16(10, true) !== LOCAL_MANIFEST_HEADER_BYTES
            || view.getUint32(12, true) !== 0
            || view.getUint16(90, true) !== LOCAL_MANIFEST_ENTRY_BYTES) {
            return decodeFailure("INVALID_HEADER", 10);
        }
        const entryCount = view.getUint32(92, true);
        if (entryCount > MAX_MANIFEST_ENTRIES)
            return decodeFailure("INVALID_COUNT", 92);
        const entriesOffset = view.getUint32(96, true);
        const bodyEndOffset = view.getUint32(100, true);
        const expectedBodyEndOffset = LOCAL_MANIFEST_HEADER_BYTES
            + entryCount * LOCAL_MANIFEST_ENTRY_BYTES;
        if (entriesOffset !== LOCAL_MANIFEST_HEADER_BYTES) {
            return decodeFailure("INVALID_OFFSET", 96);
        }
        if (bodyEndOffset !== expectedBodyEndOffset
            || bodyEndOffset + MANIFEST_TRAILER_BYTES !== input.byteLength) {
            return decodeFailure(bodyEndOffset + MANIFEST_TRAILER_BYTES < input.byteLength
                ? "TRAILING_BYTES"
                : "INVALID_LENGTH", 100);
        }
        const storedCrc = view.getUint32(bodyEndOffset, true);
        const actualCrc = checkedCrc32c(checksumProvider, input.subarray(0, bodyEndOffset));
        if (actualCrc === null || actualCrc !== storedCrc) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset);
        }
        const actualDigest = await checkedSha256(checksumProvider, input.subarray(0, bodyEndOffset + 4));
        if (actualDigest === null
            || !equalBytes(actualDigest, input.subarray(bodyEndOffset + 4))) {
            return decodeFailure("CHECKSUM_MISMATCH", bodyEndOffset + 4);
        }
        const metadata = decodeLocalMetadata(input, view);
        if (!metadata.ok)
            return metadata;
        const localRevision = metadata.value.localRevision;
        const chunks = [];
        let previous;
        for (let index = 0; index < entryCount; index += 1) {
            const offset = LOCAL_MANIFEST_HEADER_BYTES + index * LOCAL_MANIFEST_ENTRY_BYTES;
            const chunkX = checkedValues.int32(view.getInt32(offset, true));
            const chunkZ = checkedValues.int32(view.getInt32(offset + 4, true));
            const entryRevision = checkedValues.localSandboxRevision(view.getBigUint64(offset + 8, true));
            const chunkByteLength = checkedValues.uint32(view.getUint32(offset + 16, true));
            const chunkCrc = checkedValues.uint32(view.getUint32(offset + 20, true));
            const chunkSha = checkedValues.sha256Digest(input.slice(offset + 24, offset + 56));
            if (!chunkX.ok || !chunkZ.ok || !entryRevision.ok || !chunkByteLength.ok
                || !chunkCrc.ok || !chunkSha.ok) {
                return decodeFailure("INVALID_HEADER", offset);
            }
            const entry = Object.freeze({
                chunkX: chunkX.value,
                chunkZ: chunkZ.value,
                localRevision: entryRevision.value,
                chunkByteLength: chunkByteLength.value,
                chunkContainerCrc32c: chunkCrc.value,
                chunkSha256: chunkSha.value,
            });
            if (entry.chunkX < WORLD_FORMAT_V1.worldMinChunkX
                || entry.chunkX > WORLD_FORMAT_V1.worldMaxChunkX
                || entry.chunkZ < WORLD_FORMAT_V1.worldMinChunkZ
                || entry.chunkZ > WORLD_FORMAT_V1.worldMaxChunkZ
                || entry.chunkByteLength < SNAPSHOT_HEADER_BYTES + SNAPSHOT_TRAILER_BYTES
                || entry.chunkByteLength > SNAPSHOT_MAX_BYTES
                || entry.localRevision > localRevision) {
                return decodeFailure("INVALID_COUNT", offset);
            }
            if (previous !== undefined && compareChunkOrder(previous, entry) >= 0) {
                return decodeFailure("INVALID_ORDER", offset);
            }
            chunks.push(entry);
            previous = entry;
        }
        return decodeSuccess(Object.freeze({
            scope: "local_sandbox",
            metadata: metadata.value,
            localRevision,
            chunks: Object.freeze(chunks),
        }));
    }
    return Object.freeze({
        encodeModifiedSubchunk,
        decodeModifiedSubchunk,
        encodeChunkSnapshot,
        decodeChunkSnapshot,
        encodeLocalSandboxChunkSnapshot,
        decodeLocalSandboxChunkSnapshot,
        encodeWorldSnapshotManifest,
        decodeWorldSnapshotManifest,
        encodeLocalSandboxManifest,
        decodeLocalSandboxManifest,
        encodeChunkDelta,
        decodeChunkDelta,
        encodeLocalSandboxChunkDelta,
        decodeLocalSandboxChunkDelta,
    });
}
const CODEC_FACTORY_V1 = Object.freeze({
    create: createCodec,
});
export function createWorldFormatCodecFactoryV1() {
    return CODEC_FACTORY_V1;
}
